/**
 *
 * "Heatmap page with decklayout colored as wear amount"
 *
 *
 * @file   HeatMapPage.js
 * @author Lateral
 * @since  2023
 */
import { DeckCanvas } from 'components'
import { Box, Grid, MenuItem, OutlinedInput, Paper, Select, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import BomTable from 'components/tables/bom/BomTable'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useMaintenance } from 'hooks'
import { CurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'
import { DeckMode } from 'common/deckMode'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
dayjs.extend(isBetween)
import BigNumber from 'bignumber.js'
import { pickHeatColour } from 'common/heatColours'
import { dateFormat } from 'common/dates'
import { MobileDeckCanvas } from 'components/mobileDeckCanvas/MobileDeckCanvas'
import { DeckLayoutContainer } from '../common/DeckLayoutContainer'
import { useOnBomClick } from 'hooks/useOnBomClick'
import Legend from '../maintenance/mobile/components/Legend'
import { DeckMappToolTip } from 'components/tooltip/DeckMappToolTip'
import { DeckCanvasTab } from '../common/DeckCanvasTab'

function HeatMapPage() {
  /**
   * Generates Heat Map page deck laouyt, forms and buttons
   *
   * @function
   *
   * @returns {object} - Heat Map page
   */
  const [selected, setSelected] = useState([])
  const { materialSelected, onBomClick, setMaterialSelected } = useOnBomClick(() => {
    setSelected([])
  })
  const [lowWearLife, setLowWearLife] = useState(0)
  const [highWearLife, setHighWearLife] = useState(0)
  const { deckId, database } = useContext(CurrentDeckContext)
  const theme = useTheme()
  const { actions } = useMaintenance()

  const deck = useMemo(() => database.decks.find((d) => d.id === deckId), [database.decks, deckId])

  const deckRevision = useMemo(() => {
    const revisions = database.deckRevisions
      .filter((d) => d.DeckId == deckId && d.IsPublished)
      .sort((a, b) => b.RevisionNumber - a.RevisionNumber)
    if (revisions.length) {
      const revision = structuredClone(revisions[0])
      return revision
    }
  }, [deckId, database.deckRevisions])

  const maxRows = deckRevision?.Size.Rows

  const deckRevisions = useMemo(() => {
    return database.deckRevisions
      .filter((d) => d.DeckId == deckId && d.IsPublished)
      .sort((a, b) => b.RevisionNumber - a.RevisionNumber)
  }, [database.deckRevisions, deckId])

  const historyList = useMemo(() => {
    let data = deckRevisions.map((r) => {
      const histories = database.deckRevisionHistories.filter(
        (h) => h.RevisionId === r.id && h.HistoryType === 'Maintenance'
      )
      if (histories.length) {
        return histories.map((h, i) => {
          const date = dayjs(h.DatePerformed ?? h.createdAt)
          return {
            id: h.id,
            revisionNumber: r.RevisionNumber,
            maintenanceNumber: i,
            name: `${date.format(dateFormat)} - Revision ${r.RevisionNumber} - Maintenance ${i} ${
              h.ShortDescription?.length ? ` - ${h.ShortDescription}` : ''
            }`,
            date
          }
        })
      }
    })

    data = data.flat().filter((d) => d)

    return data.sort((a, b) => {
      if (a.date.isBefore(b.date)) {
        return 1
      } else if (a.date.isAfter(b.date)) {
        return -1
      } else {
        return 0
      }
    })
  }, [deckRevisions, database.deckRevisionHistories])

  const [startHistory, setStartHistory] = useState(historyList[historyList.length - 1])
  const [endHistory, setEndHistory] = useState(historyList[0])

  const heatMapData = useMemo(() => {
    const hList = historyList
      .filter((h) => h.date.isBetween(startHistory?.date, endHistory?.date, 'day', '[]'))
      .map((h) => h.id)

    const histories = database.deckRevisionHistories.filter((d) => hList.includes(d.id))
    const total = new BigNumber(histories.length)

    let timePeriod = new BigNumber(endHistory?.date.diff(startHistory?.date, 'week'))

    if (timePeriod.isEqualTo(0) || timePeriod.isNaN()) {
      timePeriod = new BigNumber(1)
    }

    const model = { Total: total, Panels: [], SideLiners: [] }

    //Heatmap data is based on the total maintenances done during a specific time period.
    deckRevision?.Panels.forEach((panel) => {
      const count = histories.filter((h) =>
        h.Details.find(
          (d) =>
            d.Panel?.Position.Column === panel.Position.Column &&
            d.Panel?.Position.Row === panel.Position.Row &&
            d.HistoryAction === actions.Replace.id
        )
      ).length

      const averageCalculation = count > 0 ? total.plus(count).dividedBy(count).toNumber() : 1

      model.Panels.push({
        Column: panel.Position.Column,
        Row: panel.Position.Row,
        MaterialNumber: panel.MaterialNumber,
        WearCount: count,
        WearCalculation: timePeriod.dividedBy(averageCalculation),
        WearCalculationNumber: timePeriod.dividedBy(averageCalculation).toNumber(),
        count,
        total: total.toNumber(),
        histories,
        endHistory,
        startHistory
      })
    })

    deckRevision?.SideLiners.forEach((sideLiner) => {
      const count = histories.filter((h) =>
        h.Details.find(
          (d) =>
            d.SideLiner?.StartPosition === sideLiner.StartPosition &&
            d.SideLiner?.Side === sideLiner.Side &&
            d.HistoryAction === actions.Replace.id
        )
      ).length

      const averageCalculation = count > 0 ? total.plus(count).dividedBy(count).toNumber() : 1

      model.SideLiners.push({
        StartPosition: sideLiner.StartPosition,
        Side: sideLiner.Side,
        MaterialNumber: sideLiner.MaterialNumber,
        WearCount: count,
        WearCalculation: timePeriod.dividedBy(averageCalculation),
        WearCalculationNumber: timePeriod.dividedBy(averageCalculation).toNumber()
      })
    })

    return model
  }, [historyList, deckRevision, endHistory, startHistory, database.deckRevisionHistories])

  //Calculate average wear lives. The outcome devided into three categories
  //1. Low (Feed)
  //2. Mid (Intermediate)
  //3. High (Discharge)
  const averageWearLives = useMemo(() => {
    let timePeriod = new BigNumber(endHistory?.date.diff(startHistory?.date, 'week'))
    const one = new BigNumber(1)

    if (timePeriod.isEqualTo(0) || timePeriod.isNaN()) {
      timePeriod = one
    }

    const feedPanels = heatMapData.Panels.filter((p) => new BigNumber(p.Row).isLessThanOrEqualTo(lowWearLife))
    const feedWearCount = new BigNumber(feedPanels.reduce((total, f) => total + f.WearCount, 0))
    const feedCalculation =
      feedPanels.length > 0 ? feedWearCount.plus(feedPanels.length).dividedBy(feedPanels.length) : one
    const feedResult = new BigNumber(timePeriod.dividedBy(feedCalculation).toFixed(2))

    const intermediatePanels = heatMapData.Panels.filter(
      (p) => new BigNumber(p.Row).isGreaterThan(lowWearLife) && new BigNumber(p.Row).isLessThanOrEqualTo(highWearLife)
    )
    const intermediateWearCount = new BigNumber(intermediatePanels.reduce((total, m) => total + m.WearCount, 0))
    const intermediateCalculation =
      intermediatePanels.length > 0
        ? intermediateWearCount.plus(intermediatePanels.length).dividedBy(intermediatePanels.length)
        : one
    const intermediateResult = new BigNumber(timePeriod.dividedBy(intermediateCalculation).toFixed(2))

    const dischargePanels = heatMapData.Panels.filter((p) => new BigNumber(p.Row).isGreaterThan(highWearLife))
    const dischargeWearCount = new BigNumber(dischargePanels.reduce((total, d) => total + d.WearCount, 0))
    const dischargeCalculation =
      dischargePanels.length > 0
        ? dischargeWearCount.plus(dischargePanels.length).dividedBy(dischargePanels.length)
        : one
    const dischargeResult = new BigNumber(timePeriod.dividedBy(dischargeCalculation).toFixed(2))

    return {
      Feed: feedResult.toNumber(),
      FeedColour: pickHeatColour(feedResult.dividedBy(timePeriod).times(100).toNumber(), theme),
      Intermediate: intermediateResult.toNumber(),
      IntermediateColour: pickHeatColour(intermediateResult.dividedBy(timePeriod).times(100), theme),
      Discharge: dischargeResult.toNumber(),
      DischargeColour: pickHeatColour(dischargeResult.dividedBy(timePeriod).times(100), theme)
    }
  }, [endHistory, startHistory, heatMapData, lowWearLife, highWearLife, theme])

  //set the limits between the low-mid-high of Wear life.
  useEffect(() => {
    if (deckRevision) {
      if (lowWearLife === 0) {
        setLowWearLife(Number(new BigNumber(deckRevision.Size.Rows).dividedBy(3).toFixed(0)))
      }
      if (highWearLife === 0) {
        setHighWearLife(Number(new BigNumber(deckRevision.Size.Rows).dividedBy(3).times(2).toFixed(0)))
      }
    }
  }, [])

  function setHistory(id, setter) {
    const history = database.deckRevisionHistories.find((d) => d.id === id)
    setter({
      id: history.id,
      date: dayjs(history.DatePerformed ?? history.createdAt)
    })
  }

  function onPanelClick(selected) {
    if (selected) {
      setSelected(Array.isArray(selected) ? selected : [selected])
    } else {
      setSelected([])
    }
    setMaterialSelected('')
  }

  //Action on changing panle selection
  function onSelectChange(data) {
    setSelected(data)
    setMaterialSelected('')
  }

  if (!deck || !deckRevision) {
    return null
  }

  if (Object.entries(deck).length <= 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography variant="h3">No Deck Revision found.</Typography>
      </Box>
    )
  }

  return (
    <DeckLayoutContainer
      headerTitle="Heat Map"
      Deck={
        <DeckCanvasTab
          threeDCanvas={
            <DeckCanvas
              data={deckRevision}
              heatMapData={heatMapData}
              selected={selected}
              materialSelected={materialSelected}
              onSelectChange={onSelectChange}
              mode={DeckMode.heatMap}
              onPanelClick={onPanelClick}
            />
          }
          twoDCanvas={
            <MobileDeckCanvas
              data={deckRevision}
              heatMapData={heatMapData}
              selected={selected}
              materialSelected={materialSelected}
              mode={DeckMode.heatMap}
              onPanelClick={onPanelClick}
            />
          }
        />
      }
      deckRevision={deckRevision}
      topRightElement={
        <Box>
          <Box mb={1}>
            <Typography className="deck-title" variant="h5" component="h5" sx={{ color: 'text.secondary' }}>
              Heat Map Details
            </Typography>
            <Grid item xs={12}>
              <Legend />
            </Grid>
            <Paper elevation={8} sx={{ margin: '0.5em 0', paddingBottom: '0.5em', backgroundImage: 'none' }}>
              <Typography
                variant="h5"
                component="h5"
                sx={{ color: 'supporting.pale', backgroundColor: 'primary.contrastText', padding: '0.5em 1em' }}>
                Select Time Frame to Compare
                <DeckMappToolTip title="Select the date range to display the replacement frequency of the panels and sideboards" />
              </Typography>

              <Stack direction="column" spacing={2} sx={{ margin: '1em' }}>
                {historyList.length ? (
                  <>
                    <Select
                      onChange={(e) => setHistory(e.target.value, setStartHistory)}
                      value={startHistory?.id}
                      sx={{ width: '100%' }}>
                      {historyList.map((s) => {
                        return (
                          <MenuItem key={s.id} value={s.id}>
                            {s.name}
                          </MenuItem>
                        )
                      })}
                    </Select>
                    <Select
                      onChange={(e) => setHistory(e.target.value, setEndHistory)}
                      value={endHistory?.id}
                      sx={{ width: '100%' }}>
                      {historyList.map((s) => {
                        return (
                          <MenuItem key={s.id} value={s.id}>
                            {s.name}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  </>
                ) : (
                  <Typography>No Maintenance done on this Deck.</Typography>
                )}
              </Stack>
              <Typography
                variant="h5"
                component="h5"
                sx={{ color: 'supporting.pale', backgroundColor: 'primary.contrastText', padding: '0.5em 1em' }}>
                Average Wear Life (Weeks)
                <DeckMappToolTip title="Displays the average wear life of the nominated stage that is selected" />
              </Typography>
              <Grid container sx={{ padding: '1em 1.3em' }}>
                <Grid item xs={4}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h5" component="h5" sx={{ color: `${theme.palette.supporting.pale}` }}>
                        Feed End:
                      </Typography>
                      <Typography>Row 1 to </Typography>
                      <OutlinedInput
                        defaultValue={lowWearLife}
                        onChange={(e) => setLowWearLife(Number(e.currentTarget.value))}
                        sx={{ height: '2em' }}
                        type="number"
                        inputProps={{ min: 1, max: `${highWearLife - 1}`, step: 1 }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h5" component="h5" sx={{ color: `${theme.palette.supporting.pale}` }}>
                        Wear Life:
                      </Typography>
                      <Typography
                        sx={{ color: `${averageWearLives.FeedColour}` }}>{`${averageWearLives.Feed} wks`}</Typography>
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h5" component="h5" sx={{ color: `${theme.palette.supporting.pale}` }}>
                        Intermediate:
                      </Typography>
                      <Typography>{`Row ${lowWearLife + 1} to `}</Typography>
                      <OutlinedInput
                        defaultValue={highWearLife}
                        onChange={(e) => setHighWearLife(Number(e.currentTarget.value))}
                        sx={{ height: '2em' }}
                        type="number"
                        inputProps={{ min: `${lowWearLife + 1}`, max: `${maxRows - 1}`, step: 1 }}
                      />
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h5" component="h5" sx={{ color: `${theme.palette.supporting.pale}` }}>
                        Wear Life:
                      </Typography>
                      <Typography sx={{ color: `${averageWearLives.IntermediateColour}` }}>
                        {`${averageWearLives.Intermediate} wks`}
                      </Typography>
                    </Stack>
                  </Stack>
                </Grid>
                <Grid item xs={4}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ padding: '0.25em 0' }}>
                      <Typography variant="h5" component="h5" sx={{ color: `${theme.palette.supporting.pale}` }}>
                        Discharge End:
                      </Typography>
                      <Typography>{`Row ${highWearLife + 1} to ${maxRows}`}</Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h5" component="h5" sx={{ color: `${theme.palette.supporting.pale}` }}>
                        Wear Life:
                      </Typography>
                      <Typography sx={{ color: `${averageWearLives.DischargeColour}` }}>
                        {`${averageWearLives.Discharge} wks`}
                      </Typography>
                    </Stack>
                  </Stack>
                </Grid>
              </Grid>
            </Paper>
          </Box>
          <BomTable
            data={deckRevision}
            selected={selected}
            materialSelected={materialSelected}
            onClick={onBomClick}
            deckMode={DeckMode.heatMap}
            heatMapData={heatMapData}
            fullHeight
          />
        </Box>
      }
    />
  )
}

export default HeatMapPage
